import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import SN_SFA_EXPENSE_REG_001_POP_02 from "./SN_SFA_EXPENSE_REG_001_POP_02";
import SN_SFA_EXPENSE_REG_001_POP_01 from "./SN_SFA_EXPENSE_REG_001_POP_01";
import { showDialog } from "@widgets/CommonDialog";
import { global, globalMenu } from "@shared/libs/Global";
import axios from "axios";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import { showPopupModal } from "@widgets/CommonModal";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import SnResizeBar from "@shared/components/SnResizeBar";
import {
  CardUseRow,
  parseShinhanCardStatement,
  pickFile,
} from "@shared/utils/cardStatementParser";
import ExcelUpload from "@widgets/commonpopup/ExcelUpload";

interface Props {
  Menuprops: menuInfo;
}

// grid1에서 신규 행일 때만 수정할 컬럼: 경비구분
const GRID1_NEW_ROW_EDIT_COLUMNS = ["fg_expense"];
// grid1에서 저장된 행도 항상 수정할 컬럼: 제목
// (sys_formgridcolumn 의 cd_column 이 dc_title 로 남아 있어도 잠기지 않도록 둘 다 둔다)
const GRID1_ALWAYS_EDIT_COLUMNS = ["nm_title", "dc_title"];

// grid2 공급대가 합계 대상: 교통비·주유비·통행주차비·식대·식대공제·기타 (식대공제는 차감)
const GRID2_DEDUCT_FIELD = "am_meal_over";
const GRID2_AMOUNT_FIELDS = [
  "am_vehicle",
  "am_fuel",
  "am_toll",
  "am_meal",
  "am_meal_over",
  "am_etc",
];

// 식대공제(초과) = 식대 − 1인 기준 식대 × 인원수 (음수면 0). 처리내용이 '외근'으로 시작하면 0.
// 1인 기준 식대는 sys_config(MEAL_UNIT_CONFIG)에서 읽고, 없으면 기본값을 쓴다.
const MEAL_UNIT_CONFIG = "SFA0001";
const DEFAULT_MEAL_UNIT = 10000;
const MEAL_OVER_SOURCE_FIELDS = ["am_meal", "qt_meal_emp", "dc_detail"];

// grid1 → grid2 필터(no_expense)는 ag-Grid 컬럼 필터라 grid2 에 실제 컬럼이 있어야 걸린다.
// 컬럼 설정에 경비번호가 없으면 숨김(H) 컬럼으로 추가한다.
const withGrid2ExpenseKey = (columns: any[] | undefined) => {
  const cols = columns ?? [];
  if (
    cols.length === 0 ||
    cols.some((col: any) => col.cd_column === "no_expense")
  )
    return cols;
  return [
    ...cols,
    {
      ...cols[0],
      cd_column: "no_expense",
      nm_column: "경비번호",
      tp_column: "SnTextBox",
      fg_visible: "H",
      no_seq: 999,
      qt_width: 100,
      yn_required: "N",
      yn_sum: "N",
      yn_drilldown: "N",
      cd_popup: null,
      cd_displaycolumn: null,
      js_combo: null,
      dc_editcon: "1 == 1",
      valueFormatter: undefined,
    },
  ];
};

// 교통편 공통코드 SFA041 의 "없음". 그리드 콤보는 빈 값을 받지 않아(SnColumnsCreate valueSetter)
// 이 코드를 고르면 onCellValueChangedL 에서 교통편과 교통 관련 칸을 비운다.
const TRANS_NONE = "0";

// 증빙이미지 팝업: 고르는 즉시 올라가거나(저장된 행) 보관되므로(저장 전 행) 되돌릴 것이 없다.
// 확인 시 "선택된 내용이 없습니다" 안내 없이 닫히게 하고(checkData), 버튼은 닫기 하나만 둔다.
const IMAGE_POPUP_OPTIONS = {
  width: "60%",
  height: "80VH",
  checkData: false,
  closeBtn: false,
  okayBtnText: "닫기",
};

// 행추가 시 기본 프로젝트(닷넷 기준 공통 프로젝트)
const COMMON_PJT = "99999";

// dc_editcon 은 참이면 잠금, 빈 값이면 항상 수정 가능. "new" 는 신규 행에서 false 로 바뀐다(SnColumnsCreate).
const withGrid1EditCon = (columns: any[] | undefined) =>
  (columns ?? []).map((col: any) => ({
    ...col,
    dc_editcon: GRID1_ALWAYS_EDIT_COLUMNS.includes(col.cd_column)
      ? ""
      : GRID1_NEW_ROW_EDIT_COLUMNS.includes(col.cd_column)
        ? "new"
        : "1 == 1",
  }));

// 경비등록
const SN_SFA_EXPENSE_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const mealUnitRef = useRef<number>(DEFAULT_MEAL_UNIT);
  // 저장 후 재조회에서 다시 선택할 행. no_line 이 없으면(신규 상세) 그 경비의 마지막 행.
  const keepFocusRef = useRef<{ no_expense?: string; no_line?: any } | null>(
    null
  );
  // 저장 전 상세 행에서 고른 증빙이미지(행 키 KeyField → 파일). 저장 후 채번된 번호로 올린다.
  const pendingFilesRef = useRef<Map<string, File[]>>(new Map());

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);
        setColums({
          grid1: withGrid1EditCon(rtn.grid.grid1),
          grid2: withGrid2ExpenseKey(rtn.grid.grid2),
        });
      } catch (error) {}
      try {
        const rtn: any = await fnGetDataSet({
          id: "sn_sfa_expense_reg_001_02",
          params: { cd_ctrl: MEAL_UNIT_CONFIG },
        });
        const unit = Number(rtn?.[0]?.[0]?.dc_ctrl);
        if (unit > 0) mealUnitRef.current = unit;
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
    onExcelUpload,
    onPrint,
  }));

  // 출력(경비대장 rpt_sfa_expense_prt_001 / 지출결의서 _002) = 선택된 경비 1건.
  // getReportDataSet 은 쿼리의 이름 파라미터가 모두 있어야 하므로 선택 조건(cd_biz/dt/cd_emp)도 빈 값으로 넘긴다.
  const onPrint = async () => {
    gridRefs.current["grid1"].current?.stopEditing();
    const no_expense =
      gridRefs.current["grid1"].current?.focusedRowNode()?.data?.no_expense;
    if (!no_expense) {
      await showDialog("alter", "선택된 내용이 없습니다. 저장된 경비를 선택해 주세요.");
      return;
    }
    if (gridRefs.current["grid2"].current?.getDisplayedRowCount() < 1) {
      await showDialog("alter", "출력할 경비상세가 없습니다.");
      return;
    }
    return { cd_biz: "", dt: "", cd_emp: "", no_expense };
  };

  // 엑셀업로드 팝업의 [파일업로드] = 신한카드 명세서(.htm)의 카드이용내역을 시트에 채운다.
  // 일자(dt) <- 이용일자, 공급대가(am_supply) <- 이용금액, 거래처(nm_cust) <- 가맹점명
  // 증빙구분은 과세(1)로 넣어 공급가액/부가세가 이용금액 기준으로 계산되게 한다.
  const onCardFileUpload = async () => {
    const file = await pickFile(".htm,.html");
    if (!file) return null;
    let cardRows: CardUseRow[];
    try {
      cardRows = await parseShinhanCardStatement(file);
    } catch (error: any) {
      await showDialog("alter", error.message);
      return null;
    }
    if (cardRows.length === 0) {
      await showDialog("alter", "카드이용내역이 없습니다.");
      return null;
    }
    return cardRows.map((card) => ({
      dt: card.dt_use,
      am_supply: card.am_use,
      fg_evid: "1",
      nm_cust: card.nm_store,
    }));
  };

  // 헤더 Excel > 업로드 = 공통 엑셀업로드 팝업. [저장] 시 시트 행을 grid2 에 행추가한다.
  const onExcelUpload = async () => {
    if (!gridRefs.current["grid1"].current?.focusedRowNode()) {
      await showDialog("alter", "선택된 행이 없습니다.");
      return;
    }

    const result = await showPopupModal({
      title: "엑셀 업로드",
      children: (
        <ExcelUpload
          Menuprops={Menuprops}
          PkFields={[]}
          tableName=""
          controllerName=""
          sheetColumns={columns.grid2}
          onFileUpload={onCardFileUpload}
          returnRowsOnSave={true}
        />
      ),
      width: "80%",
      height: "90VH",
      okayBtnText: "저장",
    });
    const excelRows: any[] = result?.excelRows ?? [];
    if (excelRows.length === 0) return;

    excelRows.forEach((row) => {
      const rowNode = gridRefs.current["grid2"].current?.addRow();
      if (!rowNode) return;
      Object.keys(row).forEach((key) => rowNode.setDataValue(key, row[key]));
    });
    return `${excelRows.length}건을 추가했습니다.`;
  };

  // 조회 파라미터: 사업장(cd_biz)·담당자(cd_emp)·기준년월(dt, yyyymm)
  // 비어 있는 조건은 쿼리에서 무시되므로 모두 비우면 전체 조회.
  // no_expense 는 빈 값으로 넘겨 상세(grid2)를 전부 받고, grid1 행 선택 시 화면에서 필터한다.
  const getSearchParams = () => {
    const formdata = searchRef.current.getFormdata() ?? {};
    return {
      ...formdata,
      cd_biz: formdata.cd_biz ?? "",
      cd_emp: formdata.cd_emp ?? "",
      dt: String(formdata.dt ?? "")
        .replace(/-/g, "")
        .slice(0, 6),
      no_expense: "",
    };
  };

  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      }
    }

    try {
      const params = {
        id: "sn_sfa_expense_reg_001_01",
        params: getSearchParams(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      // 조회하면 저장 전 행이 사라지므로 그 행에 고른 사진도 버린다.
      pendingFilesRef.current.clear();

      const data: any = await fnGetDataSet(params);
      setDataSources({
        // 조회 쿼리가 제목을 dc_title 로 내려줘도 nm_title 그리드 컬럼에 보이도록 맞춘다.
        grid1: (data[0] || []).map((row: any) => ({
          ...row,
          nm_title: row.nm_title ?? row.dc_title,
          dc_title: row.dc_title ?? row.nm_title,
        })),
        grid2: data[1] || [],
      });
    } catch (error: any) {}
  };

  // 상단 툴바 행추가/행삭제 = grid1(경비 헤더)
  const onInsert = () => {
    try {
      return gridRefs.current["grid1"].current?.addRow();
    } catch (error: any) {}
  };

  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;
    gridRefs.current["grid1"].current?.deleteSelectedRows();
  };

  // grid2(경비 상세) 행추가 시 선택된 grid1 행의 경비번호를 넣는다.
  const onBeforeAddRowL = (): any => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (!focusedNodeH) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      // 일자는 같은 경비의 마지막 상세 일자를 따르고, 첫 행이면 비워 달력으로 고르게 한다.
      let lastDt = "";
      gridRefs.current["grid2"].current?.forEachFilteredNode((node: any) => {
        if (node.data?.dt) lastDt = node.data.dt;
      });

      // 아직 저장 전인 신규 경비는 경비번호가 없다. 백엔드가 채번한 번호를
      // 이 상세에 이어 붙일 수 있게 헤더 행 키(KeyField)를 key_h 로 함께 보낸다.
      return {
        cd_firm: userInfo.cd_firm,
        no_expense: focusedNodeH.data?.no_expense,
        key_h: focusedNodeH.data?.KeyField,
        cd_user_reg: userInfo.cd_user,
        fg_evid: "1",
        dt: lastDt,
        // 식대 인원은 본인 1명으로 시작한다. 직원명 더블클릭으로 바꾼다.
        cd_emp_meal: userInfo.cd_emp,
        nm_emp_meal: userInfo.nm_emp,
        qt_meal_emp: 1,
        cd_pjt: COMMON_PJT,
        dc_evid_img: "등록",
      };
    } catch (error: any) {
      return false;
    }
  };

  // grid2 행추가 후 첫 포커스를 일자(dt) 컬럼으로 옮긴다.
  // 그리드가 requestAnimationFrame 으로 첫 편집 컬럼에 포커스를 잡으므로 그 뒤에 덮는다.
  const onAfterAddRowL = (node: any) => {
    if (node?.rowIndex == null) return;
    applyProjectInfo(node, node.data?.cd_pjt);
    requestAnimationFrame(() => {
      gridRefs.current["grid2"].current?.setFocusedCell(node.rowIndex, "dt");
    });
  };

  const findRowIndex = (grid: string, match: (data: any) => boolean) => {
    let index = -1;
    gridRefs.current[grid].current?.forEachFilteredNode((node: any) => {
      if (match(node.data)) index = node.rowIndex;
    });
    return index;
  };

  // 저장 후 재조회가 끝나면(grid1 의 0행 포커스 이후) 저장했던 경비/상세 행을 다시 선택한다.
  const onRowDataReadyH = () => {
    const keep = keepFocusRef.current;
    keepFocusRef.current = null;
    if (!keep) return;

    const indexH = findRowIndex("grid1", (d) => d?.no_expense === keep.no_expense);
    if (indexH < 0) return;
    gridRefs.current["grid1"].current?.selectAndFocusRow(indexH);

    // grid1 선택이 grid2 필터를 다시 걸며 0행을 잡으므로, 그 다음 프레임에 상세 행을 맞춘다.
    requestAnimationFrame(() => {
      let indexL = -1;
      gridRefs.current["grid2"].current?.forEachFilteredNode((node: any) => {
        if (keep.no_line == null || node.data?.no_line === keep.no_line)
          indexL = node.rowIndex;
      });
      if (indexL >= 0)
        gridRefs.current["grid2"].current?.selectAndFocusRow(indexL);
    });
  };

  const onGrid2BtnClick = async (id: string) => {
    if (id == "addRow") {
      gridRefs.current["grid2"].current?.addRow();
    }
    if (id == "deleteRow") {
      if (gridRefs.current["grid2"].current?.getDisplayedRowCount() < 1) return;
      gridRefs.current["grid2"].current?.deleteSelectedRows();
    }
  };

  // 저장 전 행에서 고른 증빙이미지를 채번된 경비번호/항번으로 공통 첨부(SnFileUploadBox 와 같은 API)에 올린다.
  // 반환: 올리지 못한 사진 수. 실패해도 경비 저장은 이미 끝났으므로 저장 자체는 성공으로 둔다.
  const uploadPendingImages = async (insertedLines: any[]) => {
    const { cd_menu, cd_module } = globalMenu.getState().activeMenuInfo;
    let failed = 0;
    for (const line of insertedLines) {
      const files = pendingFilesRef.current.get(String(line.KeyField));
      if (!files?.length) continue;
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));
      formData.append("cdFirm", userInfo.cd_firm || "");
      formData.append("cdMenu", cd_menu || "");
      formData.append("noDocu", line.no_expense);
      formData.append("noLineDocu", String(line.no_line));
      formData.append("dtDocu", "");
      formData.append("cdUser", userInfo.cd_user || "");
      formData.append("cdModule", cd_module || "");
      try {
        const res = await axios.post("/snotes/file/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            "X-TENANT-ID": userInfo.tenant_id || "DEFAULT",
          },
        });
        if (res.data?.result?.if_status === "E") failed += files.length;
      } catch (error) {
        console.error("증빙이미지 업로드 실패:", error);
        failed += files.length;
      }
    }
    pendingFilesRef.current.clear();
    return failed;
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();
      gridRefs.current["grid2"].current?.stopEditing();

      if (!gridRefs.current["grid1"].current.requiredValuesCheck())
        return false;
      if (!gridRefs.current["grid2"].current.requiredValuesCheck())
        return false;

      const expenseHChanges = gridRefs.current["grid1"].current.getChange();
      const expenseLChanges = gridRefs.current["grid2"].current.getChange();
      if (
        expenseHChanges.modifiedRowcount === 0 &&
        expenseLChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      // 신규 경비 헤더는 그리드에 사업장·담당자·년월 컬럼이 없어 조회조건/로그인 정보로 채운다.
      // 경비번호(no_expense)는 백엔드가 채번한다.
      const search = getSearchParams();
      // 제목은 테이블 컬럼(nm_title)으로 보낸다. 그리드/조회 컬럼이 dc_title 이어도 저장되도록 옮겨 담는다.
      const withTitle = (row: any) => ({
        ...row,
        nm_title: row.nm_title ?? row.dc_title ?? "",
      });
      const toHeaderRow = (row: any) => ({
        ...withTitle(row),
        cd_biz: row.cd_biz || search.cd_biz || userInfo.cd_biz,
        cd_emp: row.cd_emp || search.cd_emp || userInfo.cd_emp,
        cd_dept: row.cd_dept || userInfo.cd_dept,
        ym: row.ym || search.dt,
      });
      // 출발시각은 그리드 컬럼이 tm_depart 라 테이블 컬럼(hm_start)으로 옮겨 보낸다.
      const toLineRow = (row: any) =>
        row.tm_depart === undefined ? row : { ...row, hm_start: row.tm_depart };

      const saveData = {
        sfa_expenseh: {
          ...expenseHChanges,
          insert: (expenseHChanges.insert ?? []).map(toHeaderRow),
          update: (expenseHChanges.update ?? []).map(withTitle),
        },
        sfa_expensel: {
          ...expenseLChanges,
          insert: (expenseLChanges.insert ?? []).map(toLineRow),
          update: (expenseLChanges.update ?? []).map(toLineRow),
        },
      };

      const ok = await fnSave("SN_SFA_EXPENSE_REG_001_SAVE_01", saveData);
      if (!ok) return false;

      // 신규 경비는 채번 전이라 번호가 없다. 조회가 경비번호 역순이라 새 경비는 0행으로 오므로 기본 포커스에 맡긴다.
      const focusedH = gridRefs.current["grid1"].current?.focusedRowNode()?.data;
      const focusedL = gridRefs.current["grid2"].current?.focusedRowNode()?.data;
      keepFocusRef.current = focusedH?.no_expense
        ? { no_expense: focusedH.no_expense, no_line: focusedL?.no_line }
        : null;

      // 저장 응답(return_item) = 신규 상세별 { KeyField, no_expense, no_line }
      const failedCount = await uploadPendingImages(Array.isArray(ok) ? ok : []);

      gridRefs.current["grid1"].current?.resetUpdate();
      gridRefs.current["grid2"].current?.resetUpdate();
      // 채번된 경비번호/항번을 화면에 반영하려고 재조회한다.
      await onSearch();
      if (failedCount > 0) {
        await showDialog(
          "alter",
          `경비는 저장되었지만 증빙이미지 ${failedCount}장을 올리지 못했습니다. 해당 행의 증빙이미지에서 다시 올려 주세요.`
        );
      }
      return true;
    } catch (error: any) {
      // fnSave 는 서버 검증 오류(400)를 안내 없이 던진다. 메시지를 띄우고,
      // 입력한 변경분은 그대로 두어 고친 뒤 다시 저장할 수 있게 한다.
      await showDialog(
        "alter",
        error?.error?.response?.data?.message ?? "저장 중 오류가 발생했습니다."
      );
      return false;
    }
  };

  // 증빙구분 콤보 코드 → "과세" | "간이" | "" (코드 체계를 몰라도 되도록 콤보 명칭으로 판별)
  const getEvidenceType = (code: any): "과세" | "간이" | "" => {
    const value = String(code ?? "").trim();
    if (!value) return "";
    const evidCol = (columns.grid2 ?? []).find(
      (col: any) => col.cd_column === "fg_evid"
    );
    let comboList: any[] = [];
    try {
      comboList = Array.isArray(evidCol?.js_combo)
        ? evidCol.js_combo
        : JSON.parse(evidCol?.js_combo || "[]");
    } catch {
      comboList = [];
    }
    const name = String(
      comboList.find((item: any) => String(item.code) === value)?.name ?? ""
    );
    if (name.includes("간이")) return "간이";
    if (name.includes("과세")) return "과세";
    // 콤보 목록을 못 찾으면 기존 코드 규칙(1: 과세, 2: 간이)으로 판별
    if (!name && value.charAt(0) === "1") return "과세";
    if (!name && value.charAt(0) === "2") return "간이";
    return "";
  };

  // 증빙구분(fg_evid) 과세: 공급대가 = 합계, 공급가액 = 합계 / 1.1, 부가세 = 차액
  // 증빙구분(fg_evid) 간이: 공급대가 = 공급가액 = 합계, 부가세 = 0
  const applyEvidenceAmount = (rowData: any, rowNode: any) => {
    const evidence = getEvidenceType(rowData.fg_evid);
    if (!evidence) return;

    // 교통비~기타에 금액이 하나도 없으면(직접 입력·카드내역 업로드 행) 공급대가를 기준으로 한다
    const hasAmount = GRID2_AMOUNT_FIELDS.some(
      (field) => (Number(rowData[field]) || 0) !== 0
    );
    const total = hasAmount
      ? GRID2_AMOUNT_FIELDS.reduce(
          (sum: number, field: string) =>
            sum +
            (field === GRID2_DEDUCT_FIELD ? -1 : 1) *
              (Number(rowData[field]) || 0),
          0
        )
      : Number(rowData.am_supply) || 0;
    const supply = evidence === "과세" ? Math.round(total / 1.1) : total;

    rowNode.setDataValue("am_supply", total, "noEvent");
    rowNode.setDataValue("am_k", supply, "noEvent");
    rowNode.setDataValue("am_vat", total - supply, "noEvent");
  };

  const getMealOver = (rowData: any) => {
    if (String(rowData.dc_detail ?? "").trim().startsWith("외근")) return 0;
    const qtEmp = Number(rowData.qt_meal_emp) || 1;
    return Math.max(
      0,
      (Number(rowData.am_meal) || 0) - mealUnitRef.current * qtEmp
    );
  };

  // 프로젝트코드 입력 시 프로젝트명·고객사를 바로 채운다(저장 전에도 보이도록).
  const applyProjectInfo = async (rowNode: any, cd_pjt: any) => {
    let row: any = {};
    if (cd_pjt) {
      try {
        const rtn: any = await fnGetDataSet({
          id: "sn_sfa_expense_reg_001_03",
          params: { cd_pjt },
        });
        row = rtn?.[0]?.[0] ?? {};
      } catch (error) {}
    }
    rowNode.setDataValue("nm_pjt", row.nm_pjt ?? "", "noEvent");
    rowNode.setDataValue("nm_cust_pjt", row.nm_cust_pjt ?? "", "noEvent");
  };

  // 직원명 더블클릭 = 직원 선택 팝업(왼쪽 사원 → 오른쪽 선택된 사원). 고른 사원 수가 인원수가 되고 식대공제가 다시 계산된다.
  const onCellDoubleClickedL = async (params: any) => {
    if (params?.colDef?.field !== "nm_emp_meal") return;
    const node = params.node;
    const codes = String(node.data?.cd_emp_meal ?? "")
      .split(",")
      .filter(Boolean);
    const names = String(node.data?.nm_emp_meal ?? "").split(", ");

    const result: any = await showPopupModal({
      title: "직원 선택",
      children: (
        <SN_SFA_EXPENSE_REG_001_POP_02
          selected={codes.map((cd_emp, i) => ({
            cd_emp,
            nm_emp: names[i] ?? "",
          }))}
        />
      ),
      width: "70%",
      height: "80VH",
    });
    const rows: any[] | undefined = result?.rows;
    if (!Array.isArray(rows)) return;

    // 사번 목록은 그리드 컬럼이 아니라 행 데이터에 직접 담고, 직원명·인원수는 셀 변경으로 반영해
    // 변경 이력과 식대공제 재계산이 함께 일어나게 한다.
    node.data.cd_emp_meal = rows.map((row) => row.cd_emp).join(",");
    node.setDataValue("nm_emp_meal", rows.map((row) => row.nm_emp).join(", "));
    node.setDataValue("qt_meal_emp", rows.length);
  };

  const imageLabel = (count: number) => (count > 0 ? `보기 (${count})` : "등록");

  // 증빙이미지 칸 클릭 = 그 행의 첨부(sys_attachfile: 메뉴+경비번호+항번) 올리기/보기 팝업.
  // 저장 전 행은 번호가 없어 고른 사진을 들고 있다가 저장할 때 올린다(uploadPendingImages).
  const onCellClickedL = async ({ colId, node }: any) => {
    if (colId !== "dc_evid_img") return;
    const { no_expense, no_line, KeyField } = node?.data ?? {};
    if (!no_expense || !no_line) {
      const key = String(KeyField);
      await showPopupModal({
        title: "증빙이미지",
        children: (
          <SN_SFA_EXPENSE_REG_001_POP_01
            pendingFiles={pendingFilesRef.current.get(key) ?? []}
            onPendingChanged={(files) => {
              if (files.length > 0) pendingFilesRef.current.set(key, files);
              else pendingFilesRef.current.delete(key);
              node.setDataValue("dc_evid_img", imageLabel(files.length), "noChange");
            }}
          />
        ),
        ...IMAGE_POPUP_OPTIONS,
      });
      return;
    }
    await showPopupModal({
      title: "증빙이미지",
      children: (
        <SN_SFA_EXPENSE_REG_001_POP_01
          no_expense={no_expense}
          no_line={Number(no_line)}
          onCountChanged={(count) =>
            // 화면 표시만 바꾼다(noChange: 변경 이력에 넣지 않음)
            node.setDataValue("dc_evid_img", imageLabel(count), "noChange")
          }
        />
      ),
      ...IMAGE_POPUP_OPTIONS,
    });
  };

  const onCellValueChangedL = (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    let rowData = event.rowData;

    // 교통편을 비우면 교통 관련 칸도 함께 비운다. 콤보 셀은 setDataValue 로 비울 수 없어
    // 행 데이터를 직접 바꾸고 화면만 갱신한 뒤 공급대가를 다시 계산한다.
    if (colId === "fg_trns" && rowData.fg_trns === TRANS_NONE) {
      Object.assign(event.rowData, {
        fg_trns: "",
        tm_depart: "",
        hm_end: "",
        qt_distance: 0,
        am_vehicle: 0,
        am_fuel: 0,
        am_toll: 0,
      });
      event.rowNode.updateData({ ...event.rowData });
      applyEvidenceAmount(event.rowNode.data, event.rowNode);
      return;
    }

    if (colId === "cd_pjt") {
      applyProjectInfo(event.rowNode, rowData.cd_pjt);
    }

    if (MEAL_OVER_SOURCE_FIELDS.includes(colId)) {
      const am_meal_over = getMealOver(rowData);
      event.rowNode.setDataValue(GRID2_DEDUCT_FIELD, am_meal_over, "noEvent");
      rowData = { ...rowData, am_meal_over };
    }

    if (
      colId === "fg_evid" ||
      colId === "am_supply" ||
      MEAL_OVER_SOURCE_FIELDS.includes(colId) ||
      GRID2_AMOUNT_FIELDS.includes(colId)
    ) {
      applyEvidenceAmount(rowData, event.rowNode);
    }
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onRowDataReady: onRowDataReadyH,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRowL,
          onAfterAddRow: onAfterAddRowL,
          onCellValueChanged: onCellValueChangedL,
          onCellDoubleClicked: onCellDoubleClickedL,
          onCellClicked: onCellClickedL,
          onBtnAddClick: onGrid2BtnClick,
          onBtnDeleteClick: onGrid2BtnClick,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1 flex w-full min-w-0 space-x-1.5">
        <SnResizeBar direction={"width"} id="panel1" orgSize="29.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "경비" }}
            events={grids.grid1.events}
            // grid1 행 선택 시 같은 경비번호의 상세만 grid2 에 표시
            filter={{
              refs: [gridRefs.current["grid2"]],
              columns: ["no_expense"],
            }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"width"} id="panel2" orgSize="70%">
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={grids.grid2.columns}
            rowData={grids.grid2.dataSource}
            etcProps={{ rowSelection: "single", title: "경비상세" }}
            events={grids.grid2.events}
          />
        </SnResizeBar>
      </div>
    </div>
  );
});

export default SN_SFA_EXPENSE_REG_001;
